/* eslint-disable react/no-array-index-key */
import React, { FC } from 'react';
import { Col, Descriptions, Row} from 'antd';
import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import styled from 'styled-components';

import { PageHeader } from '~/layout';
import { getBusinessById } from '~/api/businesses';
import { useApi, useIndustry, useMCC, usePermissions } from '~/hooks';
import { Business } from '~/types';
import buildAddress from '~/utils/buildAddress';
import AddContact from './AddContact';
import { NoPermission } from '~/components';
import { auth } from '~/api/axios';
import { layoutHeaderBackground } from '~/theme/defaults';
import Title from 'antd/lib/typography/Title';

interface Props extends RouteComponentProps {
  className?: string;
}

const ProfilePage: FC<Props> = (props) => {
  const { className } = props;
  const [t] = useTranslation('profile');
  const profileId = auth.getProfile();
  const [{ data }, refetch] = useApi<Business>(getBusinessById, [profileId]);
  const { getMCC } = useMCC();
  const { getIndustry } = useIndustry();

  const hasAccessView = usePermissions('business.profile.view');
  const hasAccessUpdate = usePermissions('business.profile.update');
  if (!hasAccessView) return <NoPermission />;
  
  return (
    <div className={cx(className, 'ProfilePage')}>
      <Row gutter={gutter}>
        <Col span={12}>
          <PageHeader heading={t('profile')} showSearch={false} />
        </Col>
      </Row>
      <Row gutter={gutter} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Descriptions
          title={<Title level={5}>{t('company')}</Title>}
          bordered={true}
          labelStyle={{width:'25%'}}
          contentStyle={{backgroundColor: layoutHeaderBackground}}
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label={t('companyName')}>{data?.legalName}</Descriptions.Item>
          <Descriptions.Item label={t('legalType')}>{data?.legalType}</Descriptions.Item>
          <Descriptions.Item label={t('tradeName')}>{data?.tradeName}</Descriptions.Item>
          <Descriptions.Item label={t('localizedTradeName')}>{data?.tradeNameLocalized}</Descriptions.Item>
          <Descriptions.Item label={t('industry')}>{getIndustry(data?.industry)}</Descriptions.Item>
          <Descriptions.Item label={t('merchantCategoryCode')}>{getMCC(data?.merchantCategoryCode)}</Descriptions.Item>
          <Descriptions.Item label={t('address')}>{buildAddress(data?.address)}</Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
      <Row gutter={gutter} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Descriptions
            title={<Title level={5}>{t('contacts')}</Title>}
            bordered
            labelStyle={{width:'25%'}}
            contentStyle={{backgroundColor: layoutHeaderBackground}}
            column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
          >
            <Descriptions.Item label={t('address')}>{buildAddress(data?.address)}</Descriptions.Item>
            {data?.contacts?.map(item => {
              return (
                <Descriptions.Item 
                    key={item.id}
                    label={t(item.channel || 'Unnamed channgel')}
                  >
                    {item.id}
                </Descriptions.Item>
              );
            })}
          </Descriptions>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col span={24}>
          {hasAccessUpdate && <AddContact record={data} refetch={refetch} />}
        </Col>
      </Row>
    </div>
  );
};

const gutter: [number, number] = [16, 24];

export default styled(ProfilePage)`
  .ant-descriptions-header {
    margin-bottom: 8px;
  }
  h5 {
    margin-bottom: 0;
  }
`;
